import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Divider from '@mui/material/Divider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import Tooltip from '@mui/material/Tooltip';
import { PosicaoAtualAtivoItem } from '../../../../types/carteiraDiaria';
import { GrupoFundo } from '../../../../types/ativoMaster';
import { formatCodigo, formatBRL, formatPercent, formatRentSimples } from '../../../../utils/formatters';
import { formatCdiSpread } from '../../../../utils/expectedReturn';
import { getGrupoFundoById } from '../../../../services/ativoMasterService';
import AtivoTableRow from './AtivoTableRow';

const thSx = {
  backgroundColor: '#F4F7F9',
  color: '#4D5766',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  borderBottom: '2px solid #CFD8E5',
  whiteSpace: 'normal' as const,
  overflow: 'visible' as const,
  lineHeight: 1.3,
};

const ellipsis = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

export interface AssetGroup {
  key: string;
  label: string;
  subLabel?: string;
  grupoFundoId?: number;
  totalConcentracao: number;
  totalValorFinanceiro: number;
  totalVariacaoFinanceira: number;
  hasVariacaoFinanceira: boolean;
  hasAtivosNegativos: boolean;
  ativosNegativosNomes: string[];
  hasDesvioEsperado: boolean;
  ativosDesvioNomes: string[];
  items: Array<{ ativo: PosicaoAtualAtivoItem; flatIndex: number; hasDesvio?: boolean }>;
  isMapped: boolean;
}

export interface AssetGroupCardProps {
  group: AssetGroup;
  isExpanded: boolean;
  onToggle: (key: string) => void;
  fundoId: number;
  expandedRowKey: string | null;
  onToggleRow: (key: string) => void;
  onOpenCadastro: (ativo: PosicaoAtualAtivoItem) => void;
  indicadoresDiarios?: Record<string, number> | null;
}

const rentColor = (value: number | null): string => {
  if (value == null || value === 0) return '#8A98A8';
  return value > 0 ? '#29A152' : '#E51739';
};

const AssetGroupCard: React.FC<AssetGroupCardProps> = ({
  group, isExpanded, onToggle, fundoId, expandedRowKey, onToggleRow, onOpenCadastro, indicadoresDiarios,
}) => {
  // ── Rentabilidade do grupo ────────────────────────────────────────────────────
  //
  // Fórmula: média ponderada pelo valor financeiro de cada ativo
  //
  //   rent_grupo = Σ (rent_i × valor_total_i) / Σ valor_total_i
  //
  // onde rent_i = rent_cota_corrigida = (cota_hoje − cota_ontem + amort_por_cota) / cota_ontem
  //
  // A amortização/juros do dia já está embutida em rent_cota_corrigida (vem do backend).
  // Usar a cota como base torna o cálculo imune a aportes e resgates do dia —
  // mudanças de quantidade não afetam o preço da cota.
  //
  // Fallback: quando rent_cota_corrigida não está disponível (ativo sem preço anterior),
  // usa Σ variacao_financeira / PL_antes — o cálculo antigo, que pode distorcer em dias
  // de amortização mas é melhor que mostrar zero.
  const itemsComRentCorrigida = group.items.filter(({ ativo }) => ativo.rent_cota_corrigida != null);
  const groupRentPct = itemsComRentCorrigida.length > 0
    ? itemsComRentCorrigida.reduce((s, { ativo }) => s + (ativo.rent_cota_corrigida! * (ativo.valor_total ?? 0)), 0)
      / itemsComRentCorrigida.reduce((s, { ativo }) => s + (ativo.valor_total ?? 0), 1)
    : (() => {
        const plAntes = group.totalValorFinanceiro - group.totalVariacaoFinanceira;
        return group.hasVariacaoFinanceira && plAntes !== 0
          ? group.totalVariacaoFinanceira / plAntes
          : null;
      })();

  // Variação em R$ derivada do percentual corrigido: var = rent × PL_hoje / (1 + rent)
  // Derivar do percentual garante consistência — evita mostrar BRL negativo com % positivo
  // em dias de amortização onde variacao_financeira bruta é negativa.
  const groupVarBrl = groupRentPct != null
    ? groupRentPct * group.totalValorFinanceiro / (1 + groupRentPct)
    : null;

  // Carrega detalhes do GrupoFundo (gestor, administrador, contatos) na primeira expansão.
  // Só busca quando o card é de um grupo com GrupoFundo cadastrado.
  const [grupoDetail, setGrupoDetail] = useState<GrupoFundo | null>(null);
  const [detailLoaded, setDetailLoaded] = useState(false);

  useEffect(() => {
    if (isExpanded && group.grupoFundoId && !detailLoaded) {
      getGrupoFundoById(group.grupoFundoId)
        .then(data => setGrupoDetail(data))
        .catch(() => setGrupoDetail(null))
        .finally(() => setDetailLoaded(true));
    }
  }, [isExpanded, group.grupoFundoId, detailLoaded]);

  // Só renderiza o painel se houver ao menos uma informação para mostrar.
  const hasInfo = grupoDetail && (
    grupoDetail.gestor ||
    grupoDetail.administrador ||
    (grupoDetail.contatos && grupoDetail.contatos.length > 0)
  );

  const borderColor = group.hasAtivosNegativos ? '#E51739' : group.hasDesvioEsperado ? '#F5A623' : '#CFD8E5';
  const bgColor     = group.hasAtivosNegativos ? '#FFF5F6' : group.hasDesvioEsperado ? '#FFFBF2' : '#EEF4FC';
  const bgHover     = group.hasAtivosNegativos ? '#FFECEE' : group.hasDesvioEsperado ? '#FFF6E0' : '#E4EEF8';

  return (
  <Paper elevation={0} sx={{
    border: `1px solid ${borderColor}`,
    borderRadius: 2, overflow: 'hidden', mb: 1,
  }}>
    {/* Cabeçalho clicável */}
    <Box
      onClick={() => onToggle(group.key)}
      sx={{
        display: 'flex', alignItems: 'center', px: 2, py: 1.25,
        cursor: 'pointer',
        backgroundColor: bgColor,
        '&:hover': { backgroundColor: bgHover },
        userSelect: 'none',
      }}
    >
      {/* Arrow + dot + name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {isExpanded
          ? <KeyboardArrowUpIcon sx={{ fontSize: 18, color: '#4D5766', flexShrink: 0 }} />
          : <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#4D5766', flexShrink: 0 }} />
        }
        <Box sx={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
          backgroundColor: group.isMapped ? '#29A152' : '#F5A623',
        }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1A2332', ...ellipsis }}>
            {group.label}
          </Typography>
          {group.subLabel && (
            <Typography sx={{ fontSize: '11px', color: '#6B7A8D', fontFamily: 'monospace' }}>
              {formatCodigo(group.subLabel)}
            </Typography>
          )}
        </Box>
        {(() => {
          const temFechado = group.items.some(i => i.ativo.condominio === 'FECHADO');
          const temAberto  = group.items.some(i => i.ativo.condominio === 'ABERTO');
          const bg     = temFechado ? '#F3EFF9' : temAberto ? '#EBF7FA' : '#fff';
          const border = temFechado ? '#C4AEE8' : temAberto ? '#9DD8E8' : '#B0C4D8';
          const color  = temFechado ? '#7B5EA7' : temAberto ? '#2C91AD' : '#4D5766';
          return (
            <Box sx={{ px: 1.5, py: 0.25, borderRadius: 1, ml: 1.5, flexShrink: 0, backgroundColor: bg, border: `1px solid ${border}` }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', color }}>
                {group.items.length} ativo{group.items.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          );
        })()}
        {group.hasAtivosNegativos && (
          <Tooltip title={`Rent. negativa ou zero: ${group.ativosNegativosNomes.join(', ')}`}>
            <WarningAmberIcon sx={{ fontSize: 16, color: '#F5A623', flexShrink: 0, ml: 1 }} />
          </Tooltip>
        )}
        {group.hasDesvioEsperado && (
          <Tooltip title={`Rent. diferente do esperado: ${group.ativosDesvioNomes.join(', ')}`}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#F5A623', flexShrink: 0, ml: 1 }} />
          </Tooltip>
        )}
      </Box>

      {/* Stat boxes */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, ml: 2 }}>
        {group.hasVariacaoFinanceira && (
          <Box sx={{ textAlign: 'right' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
              <Typography sx={{ fontSize: '10px', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'Inter' }}>
                Rent. do Dia
              </Typography>
              <Tooltip
                title="Calculado como: quantidade × (preço hoje − preço ontem), somado para os ativos do grupo. O percentual é sobre o valor do grupo no início do dia. Ativos sem preço anterior (posições novas) não são incluídos."
                placement="top"
                arrow
              >
                <InfoOutlinedIcon sx={{ fontSize: 11, color: '#B0BEC5', cursor: 'default' }} />
              </Tooltip>
            </Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, fontFamily: 'Inter', color: rentColor(groupRentPct) }}>
              {formatRentSimples(groupRentPct)}
            </Typography>
            <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', color: rentColor(groupVarBrl) }}>
              {formatBRL(groupVarBrl)}
            </Typography>
            {formatCdiSpread(groupRentPct, indicadoresDiarios?.['CDI'] ?? null) && (
              <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', color: '#8A98A8', fontStyle: 'italic' }}>
                {formatCdiSpread(groupRentPct, indicadoresDiarios?.['CDI'] ?? null)}
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: '10px', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'Inter' }}>
            Valor Financeiro
          </Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1A2332', fontFamily: 'Inter' }}>
            {formatBRL(group.totalValorFinanceiro)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', minWidth: 80 }}>
          <Typography sx={{ fontSize: '10px', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'Inter' }}>
            Concentração
          </Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1A2332', fontFamily: 'Inter' }}>
            {formatPercent(group.totalConcentracao)}
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* Painel de informações do GrupoFundo — visível apenas quando expandido e com dados */}
    {isExpanded && hasInfo && (
      <Box sx={{ px: 2, py: 1.25, backgroundColor: '#FAFBFC', borderTop: '1px solid #E8EDF2' }}>

        {/* Gestor e Administrador */}
        {(grupoDetail!.gestor || grupoDetail!.administrador) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: grupoDetail!.contatos?.length ? 1 : 0 }}>
            {grupoDetail!.gestor && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <BusinessIcon sx={{ fontSize: 13, color: '#6B7A8D' }} />
                <Typography sx={{ fontSize: '11px', color: '#6B7A8D', fontFamily: 'Inter', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Gestor
                </Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1A2332', fontFamily: 'Inter' }}>
                  {grupoDetail!.gestor}
                </Typography>
              </Box>
            )}
            {grupoDetail!.administrador && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <BusinessIcon sx={{ fontSize: 13, color: '#6B7A8D' }} />
                <Typography sx={{ fontSize: '11px', color: '#6B7A8D', fontFamily: 'Inter', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Administrador
                </Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1A2332', fontFamily: 'Inter' }}>
                  {grupoDetail!.administrador}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Contatos */}
        {grupoDetail!.contatos && grupoDetail!.contatos.length > 0 && (
          <>
            {(grupoDetail!.gestor || grupoDetail!.administrador) && (
              <Divider sx={{ my: 1, borderColor: '#E8EDF2' }} />
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {grupoDetail!.contatos.map(c => (
                <Box
                  key={c.id}
                  sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1,
                    px: 1.5, py: 0.75, borderRadius: 1.5,
                    backgroundColor: '#fff', border: '1px solid #E0E7EF',
                  }}
                >
                  <PersonIcon sx={{ fontSize: 14, color: '#8A98A8', mt: 0.1, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1A2332', fontFamily: 'Inter', lineHeight: 1.3 }}>
                      {c.nome}
                      {c.cargo && (
                        <Typography component="span" sx={{ fontWeight: 400, color: '#6B7A8D', fontSize: '11px', ml: 0.5 }}>
                          · {c.cargo}
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.25 }}>
                      {c.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <EmailIcon sx={{ fontSize: 11, color: '#8A98A8' }} />
                          <Typography
                            component="a"
                            href={`mailto:${c.email}`}
                            onClick={e => e.stopPropagation()}
                            sx={{ fontSize: '11px', color: '#2C91AD', fontFamily: 'Inter', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {c.email}
                          </Typography>
                        </Box>
                      )}
                      {c.telefone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <PhoneIcon sx={{ fontSize: 11, color: '#8A98A8' }} />
                          <Typography sx={{ fontSize: '11px', color: '#4D5766', fontFamily: 'Inter' }}>
                            {c.telefone}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    )}

    {/* Tabela de ativos expandida */}
    {isExpanded && (
      <TableContainer component={Paper} elevation={0} sx={{ borderTop: '1px solid #CFD8E5' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', '& .MuiTableCell-root': { px: 1.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...thSx, width: 70 }} />
              <TableCell sx={{ ...thSx, width: 110, pl: 1 }}>Código</TableCell>
              <TableCell sx={{ ...thSx, width: 130 }}>Nome</TableCell>
              <TableCell sx={{ ...thSx, width: 110 }}>Classe</TableCell>
              <TableCell sx={{ ...thSx, width: 55 }}>Subclasse</TableCell>
              <TableCell sx={{ ...thSx, width: 95, textAlign: 'right' }}>Qtd. Cotas</TableCell>
              <TableCell sx={{ ...thSx, width: 85, textAlign: 'right' }}>Valor da Cota</TableCell>
              <TableCell sx={{ ...thSx, width: 110, textAlign: 'right' }}>Valor Financeiro</TableCell>
              <TableCell sx={{ ...thSx, width: 90, textAlign: 'right' }}>Rentabilidade</TableCell>
              <TableCell sx={{ ...thSx, width: 80, textAlign: 'right' }}>Concentração</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {group.items.map(({ ativo, flatIndex, hasDesvio }) => (
              <AtivoTableRow
                key={`row_${flatIndex}`}
                ativo={ativo}
                flatIndex={flatIndex}
                fundoId={fundoId}
                isExpanded={expandedRowKey === `row_${flatIndex}`}
                onToggle={onToggleRow}
                onOpenCadastro={onOpenCadastro}
                hasDesvio={hasDesvio}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Paper>
  );
};

export default AssetGroupCard;
